/** @format */

import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import "./index.scss";
import { getHeaderConfig } from "./config";
import { setStepMaster } from "../../../../reducer/cfApprovalV2";
import Button from "@mui/material/Button";
import CustomModal from "../../../common-utilities/core/modal";
import { invokeApi } from "../../utils/http-service";
import {
  CF_APPROVAL_HOSTNAME,
  INTRANETHOSTNAME,
} from "../../../common-utilities/utils/end-points";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { toast } from "react-toastify";
import { HTTP_METHODS } from "../../../common-utilities/utils/http-service";
import MuiTable from "../../../common-utilities/core/mui-table";
import { REST_URLS } from "../../../common-utilities/utils/end-points/cf-approval";
import { REST_URLS as INTRANET_URLS } from "../../../common-utilities/utils/end-points/intranet";
import { REGION_OPTIONS } from "../cf-v2-create-form/config";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import InputLabel from "@mui/material/InputLabel";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Typography from "@mui/material/Typography";
import { CF_STATUS_OPTIONS } from "../cf-v2-forms-status-tabs/v2-config";
import { storeTypes } from "../../utils/constants";

const APPROVER_ROLE_OPTIONS = ["BUSINESS_FINANCE", "RM", "BD_LEAD", "CF_PROJECT_APPROVER", "BD_REGIONAL_HEAD", "LICENCE_TEAM", "LEGAL_TEAM"];
// All USER approvers of a step: the legacy single approverUserId plus the approverUserIds list.
const getStepApproverUserIds = (stepMaster) =>
  [...new Set([stepMaster?.approverUserId, ...(stepMaster?.approverUserIds || [])].filter((id) => id != null && id !== "").map(Number))];

const INPUT_FIELD_TYPES = ["DROPDOWN", "UPLOAD", "DATE", "NUMBER", "TEXT", "BOOLEAN", "REPEATABLE"];
// Sub-field types supported inside a REPEATABLE entry — mirrors what renderItemField
// in cf-v2-step-input-form.js actually renders (no nested DROPDOWN/REPEATABLE support there).
const ITEM_FIELD_TYPES = ["UPLOAD", "DATE", "NUMBER", "TEXT"];

const EMPTY_INPUT_FIELD = {
  key: "",
  type: "DROPDOWN",
  label: "",
  options: [],
  required: true,
  accept: "application/pdf",
  fileUpload: null,
};

const EMPTY_ITEM_FIELD = {
  key: "",
  type: "UPLOAD",
  label: "",
  required: true,
  accept: "application/pdf",
};

const filters = {
  page: 1,
  limit: 20,
  sortBy: "stepNumber",
  paginate: true,
};

// storeType selects which step-master collection this instance manages (CfStepMaster for
// cocoBuild, CfAcquiredStepMaster for cocoAcquired) — both now have a canonical seed config
// (DEFAULT_STEP_CONFIG / DEFAULT_ACQUIRED_STEP_CONFIG), so both get the same Seed/Reconcile flow.
// Only cocoBuild's steps are pushed into Redux — that store feeds screens for the only CF flow
// that's live today.
const StepMaster = ({ storeType = storeTypes.cocoBuild }) => {
  const isCocoBuild = storeType === storeTypes.cocoBuild;
  const dispatch = useDispatch();
  const [data, setData] = useState({});
  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(false);
  const [seedLoading, setSeedLoading] = useState(false);
  const [approverUserOptions, setApproverUserOptions] = useState([]);
  // editableFilesObject is edited as raw JSON text (parsed on save).
  const [editableFilesText, setEditableFilesText] = useState("");

  // Form fields for editing
  const [formData, setFormData] = useState({
    name: "",
    approverType: "USER",
    approverUserIds: [],
    approverRole: [],
    regionalApprovers: [],
    canSendBackTo: [],
    sendEmailTo: "",
    isActive: true,
    inputFields: [],
    // Reminder-cron overrides — "" means "use the global default" (config.cfReminder).
    reminderHours: "",
    maxReminders: "",
    autoApproveAfterStale: false,
    autoApproveHours: "",
  });

  useEffect(() => {
    loadData(filters);
  }, [storeType]);

  const loadData = (params) => {
    setLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_STEP_MASTER}`,
      null,
      { ...params, storeType },
    )
      .then((response) => {
        if (Array.isArray(response)) {
          setData({ result: response, total: response.length });
        } else if (response?.result) {
          setData(response);
        } else {
          setData({ result: [], total: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to load step configurations");
        setLoading(false);
      });
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${INTRANET_URLS.FILTER_USER}`,
      null,
      { appLink: "cf-approval-v2", page: 1, limit: 100 },
    ).then((res) => {
      if (Array.isArray(res?.results)) {
        setApproverUserOptions(
          res.results.map((u) => ({ label: `${u.name}(${u.email})`, value: u.id })),
        );
      }
    });
  };

  // Refresh the redux copy (full list, unpaginated) so other views stay in sync. cocoBuild-only:
  // that redux slice feeds screens for the only CF flow that's actually live today.
  const refreshReduxStepMaster = () => {
    if (!isCocoBuild) return;
    invokeApi(HTTP_METHODS.GET, `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_STEP_MASTER}`, null, { storeType })
      .then((steps) => {
        const all = Array.isArray(steps) ? steps : steps?.result || [];
        dispatch(setStepMaster(all));
      })
      .catch(() => {});
  };

  const seedDefaults = () => {
    setSeedLoading(true);
    invokeApi(
      HTTP_METHODS.POST,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_STEP_MASTER_SEED}`,
      {},
      { storeType },
    )
      .then((response) => {
        toast.success("Default step configurations seeded successfully!");
        loadData(filters);
        refreshReduxStepMaster();
        setSeedLoading(false);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to seed default configurations");
        setSeedLoading(false);
      });
  };

  const handleClick = (data, type) => {
    switch (type) {
      case "EDIT":
        setEditData(data);
        setFormData({
          name: data.name || "",
          approverType: data.approverType || "USER",
          approverUserIds: getStepApproverUserIds(data),
          approverRole: data.approverRole || [],
          regionalApprovers: data.regionalApprovers || [],
          canSendBackTo: data.canSendBackTo || [],
          sendEmailTo: data.sendEmailTo || "",
          isActive: data.isActive !== undefined ? data.isActive : true,
          cfStatus: data.cfStatus || "LEAD",
          inputFields: data.inputFields || [],
          reminderHours: data.reminderHours ?? "",
          maxReminders: data.maxReminders ?? "",
          autoApproveAfterStale: !!data.autoApproveAfterStale,
          autoApproveHours: data.autoApproveHours ?? "",
        });
        setEditableFilesText(
          data.editableFilesObject && Object.keys(data.editableFilesObject).length
            ? JSON.stringify(data.editableFilesObject, null, 2)
            : ""
        );
        setEditModal(true);
        break;
      case "VIEW":
        setEditData(data);
        setViewModal(true);
        break;
      default:
        break;
    }
  };

  const handleFormChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // ─── inputFields editor helpers ──────────────────────────────────────────
  const updateInputField = (idx, patch) => {
    setFormData((prev) => {
      const next = prev.inputFields.map((f, i) => (i === idx ? { ...f, ...patch } : f));
      return { ...prev, inputFields: next };
    });
  };

  const addInputField = () => {
    setFormData((prev) => ({
      ...prev,
      inputFields: [...prev.inputFields, { ...EMPTY_INPUT_FIELD }],
    }));
  };

  const removeInputField = (idx) => {
    setFormData((prev) => ({
      ...prev,
      inputFields: prev.inputFields.filter((_, i) => i !== idx),
    }));
  };

  const toggleConditionalUpload = (idx, enabled) => {
    updateInputField(
      idx,
      enabled
        ? { fileUpload: { key: "", label: "", whenValueIn: [], accept: "application/pdf" } }
        : { fileUpload: null }
    );
  };

  const updateFileUpload = (idx, patch) => {
    setFormData((prev) => {
      const next = prev.inputFields.map((f, i) =>
        i === idx ? { ...f, fileUpload: { ...(f.fileUpload || {}), ...patch } } : f
      );
      return { ...prev, inputFields: next };
    });
  };

  // ─── REPEATABLE fields: itemFields editor helpers ────────────────────────
  const addItemField = (idx) => {
    setFormData((prev) => {
      const next = prev.inputFields.map((f, i) =>
        i === idx ? { ...f, itemFields: [...(f.itemFields || []), { ...EMPTY_ITEM_FIELD }] } : f
      );
      return { ...prev, inputFields: next };
    });
  };

  const removeItemField = (idx, itemIdx) => {
    setFormData((prev) => {
      const next = prev.inputFields.map((f, i) =>
        i === idx ? { ...f, itemFields: (f.itemFields || []).filter((_, j) => j !== itemIdx) } : f
      );
      return { ...prev, inputFields: next };
    });
  };

  const updateItemField = (idx, itemIdx, patch) => {
    setFormData((prev) => {
      const next = prev.inputFields.map((f, i) =>
        i === idx
          ? {
              ...f,
              itemFields: (f.itemFields || []).map((itf, j) => (j === itemIdx ? { ...itf, ...patch } : itf)),
            }
          : f
      );
      return { ...prev, inputFields: next };
    });
  };

  // showWhen: makes this field (visibility + required/upload enforcement) conditional on
  // another field's value, e.g. only show/require "registerAgreements" when the
  // "registerAgreement" dropdown is "Available". See isFieldVisible in cf-v2-step-input-form.js.
  const toggleShowWhen = (idx, enabled) => {
    updateInputField(idx, { showWhen: enabled ? { key: "", valueIn: [] } : null });
  };

  const updateShowWhen = (idx, patch) => {
    setFormData((prev) => {
      const next = prev.inputFields.map((f, i) =>
        i === idx ? { ...f, showWhen: { ...(f.showWhen || {}), ...patch } } : f
      );
      return { ...prev, inputFields: next };
    });
  };

  // Comma-string <-> array helpers for options / whenValueIn editing.
  const toCsv = (arr) => (Array.isArray(arr) ? arr.join(", ") : "");
  const fromCsv = (str) => str.split(",").map((s) => s.trim()).filter(Boolean);

  // ─── editableFilesObject (per-key edit-lock) ───────────────────────────────
  // Per-input-key map { <fieldKey>: bool } controlling whether an input may be edited/replaced
  // AFTER submission. A key is editable unless explicitly `false`. It's edited as raw JSON in a
  // text area (parsed on save). `editableKeys` is just a hint of the keys available on this step.
  const editableKeys = (formData.inputFields || []).flatMap((f) => {
    const keys = f.key ? [f.key] : [];
    if (f.type === "DROPDOWN" && f.fileUpload) keys.push(f.fileUpload.key || `${f.key}File`);
    return keys;
  });

  const formSubmitHandler = () => {
    if (!formData.name?.trim()) {
      toast.error("Step name is required");
      return;
    }

    if (formData.approverType === "USER" && !formData.approverUserIds.length) {
      toast.error("Please select at least one approver user");
      return;
    }

    if (formData.approverType === "REGIONAL_USER" && formData.regionalApprovers.length === 0) {
      toast.error("Please add at least one region-user mapping");
      return;
    }

    if (
      formData.approverType === "REGIONAL_USER" &&
      formData.regionalApprovers.some((ra) => !ra.region || !ra.approverUserId)
    ) {
      toast.error("Each region entry must have both a region and a user selected");
      return;
    }

    if (
      formData.approverType === "ROLE" &&
      formData.approverRole.length === 0
    ) {
      toast.error("Please select at least one team member");
      return;
    }

    // Parse the editableFilesObject JSON text. Empty = no locks; invalid JSON aborts the save.
    let editableFilesObject = {};
    const editableFilesTrimmed = (editableFilesText || "").trim();
    if (editableFilesTrimmed) {
      try {
        editableFilesObject = JSON.parse(editableFilesTrimmed);
      } catch (e) {
        toast.error("Editable Files must be valid JSON");
        return;
      }
      if (
        typeof editableFilesObject !== "object" ||
        Array.isArray(editableFilesObject) ||
        editableFilesObject === null
      ) {
        toast.error('Editable Files must be a JSON object, e.g. {"key": false}');
        return;
      }
    }

    const payload = {
      name: formData.name,
      approverType: formData.approverType,
      // First selected user keeps the legacy approverUserId; the rest go in approverUserIds.
      // Any one of them can approve the step.
      approverUserId:
        formData.approverType === "USER" ? formData.approverUserIds[0] ?? null : null,
      approverUserIds:
        formData.approverType === "USER" ? formData.approverUserIds.slice(1) : [],
      approverRole:
        formData.approverType === "ROLE" ? formData.approverRole : [],
      regionalApprovers:
        formData.approverType === "REGIONAL_USER" ? formData.regionalApprovers.map((ra) => ({ region: ra.region, approverUserId: ra.approverUserId })) : [],
      canSendBackTo: formData.canSendBackTo || [],
      sendEmailTo: formData.sendEmailTo || "",
      editableFilesObject,
      cfStatus: formData.cfStatus,
      reminderHours: formData.reminderHours === "" ? null : Number(formData.reminderHours),
      maxReminders: formData.maxReminders === "" ? null : Number(formData.maxReminders),
      autoApproveAfterStale: !!formData.autoApproveAfterStale,
      autoApproveHours: formData.autoApproveHours === "" ? null : Number(formData.autoApproveHours),
      inputFields: (formData.inputFields || []).map((f) => ({
        key: f.key,
        type: f.type,
        label: f.label,
        required: !!f.required,
        ...(f.type === "DROPDOWN" ? { options: f.options || [] } : { accept: f.accept || null }),
        ...(f.type === "DROPDOWN" && f.fileUpload
          ? {
              fileUpload: {
                key: f.fileUpload.key || `${f.key}File`,
                label: f.fileUpload.label || f.label,
                whenValueIn: f.fileUpload.whenValueIn || [],
                accept: f.fileUpload.accept || "application/pdf",
              },
            }
          : {}),
        ...(f.type === "REPEATABLE"
          ? {
              itemFields: (f.itemFields || []).map((itf) => ({
                key: itf.key,
                type: itf.type,
                label: itf.label,
                required: !!itf.required,
                ...(itf.type === "UPLOAD" ? { accept: itf.accept || "application/pdf" } : {}),
              })),
            }
          : {}),
        showWhen:
          f.showWhen && f.showWhen.key ? { key: f.showWhen.key, valueIn: f.showWhen.valueIn || [] } : null,
      })),
    };

    invokeApi(
      HTTP_METHODS.PUT,
      `${CF_APPROVAL_HOSTNAME}${REST_URLS.CF_V2_STEP_MASTER}/${editData.stepNumber}`,
      payload,
      { storeType },
    )
      .then((response) => {
        if (response.message) {
          toast.error(response.message);
        } else {
          toast.success("Step configuration updated successfully");
          loadData(filters);
          refreshReduxStepMaster();
          setEditModal(false);
          setEditData({});
          setFormData({
            name: "",
            approverType: "USER",
            approverUserIds: [],
            approverRole: [],
            regionalApprovers: [],
            canSendBackTo: [],
            sendEmailTo: "",
            isActive: true,
            inputFields: [],
            reminderHours: "",
            maxReminders: "",
            autoApproveAfterStale: false,
            autoApproveHours: "",
          });
          setEditableFilesText("");
        }
      })
      .catch((err) => {
        console.log(err);
        toast.error("Failed to update step configuration");
      });
  };

  return (
    <Box sx={{ width: "100%", mt: 2 }}>
      <Stack
        direction="row"
        spacing={2}
        justifyContent="flex-end"
        sx={{ mb: 2 }}
      >
        {/* Always available: seed is an idempotent upsert/reconcile against this storeType's
            canonical config, so existing environments can pull in renamed/added steps. */}
        <Button
          variant="contained"
          color="primary"
          onClick={seedDefaults}
          disabled={seedLoading}
        >
          {seedLoading ? (
            <CircularProgress size={24} />
          ) : (
            "Seed / Reconcile Configs"
          )}
        </Button>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <MuiTable
            columnsList={getHeaderConfig(handleClick, approverUserOptions)}
            dataList={data?.result || []}
            pageCount={1}
            tableConfig={{
              pagination: true,
              sorting: false,
            }}
          />
        </>
      )}

      {/* Edit Modal */}
      {editModal && (
        <CustomModal
          onClose={() => setEditModal(false)}
          title={`Edit Step ${editData.stepNumber}`}
          size="lg"
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Step Name"
              value={formData.name}
              onChange={(e) => handleFormChange("name", e.target.value)}
              fullWidth
              size="small"
            />

            <FormControl fullWidth size="small">
              <FormLabel>Approver Type</FormLabel>
              <Select
                value={formData.approverType}
                onChange={(e) =>
                  handleFormChange("approverType", e.target.value)
                }
              >
                <MenuItem value="USER">User</MenuItem>
                <MenuItem value="ROLE">Role</MenuItem>
                <MenuItem value="REGIONAL_USER">Regional User</MenuItem>
              </Select>
            </FormControl>

            {formData.approverType === "USER" && (
              <Autocomplete
                options={approverUserOptions}
                getOptionLabel={(option) => option?.label || ""}
                multiple
                filterSelectedOptions
                value={formData.approverUserIds
                  .map((id) => approverUserOptions.find((o) => +o.value === +id))
                  .filter(Boolean)}
                onChange={(e, val) => {
                  handleFormChange("approverUserIds", val.map((v) => +v.value));
                }}
                size="small"
                fullWidth
                renderInput={(params) => (
                  <TextField {...params} label="Approved Users (any one can approve)" size="small" />
                )}
              />
            )}

            {formData.approverType === "REGIONAL_USER" && (
              <Box>
                <FormLabel sx={{ mb: 1, display: "block" }}>Region → User Mappings</FormLabel>
                {formData.regionalApprovers.map((ra, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={ra.region || ""}
                        displayEmpty
                        onChange={(e) => {
                          const updated = [...formData.regionalApprovers];
                          updated[idx] = { ...updated[idx], region: e.target.value };
                          handleFormChange("regionalApprovers", updated);
                        }}
                      >
                        <MenuItem value="" disabled>Region</MenuItem>
                        {REGION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Autocomplete
                      options={approverUserOptions}
                      getOptionLabel={(option) => option?.label || ""}
                      value={approverUserOptions.find((o) => +o.value === +ra.approverUserId) || null}
                      onChange={(e, val) => {
                        const updated = [...formData.regionalApprovers];
                        updated[idx] = { ...updated[idx], approverUserId: val ? val.value : null };
                        handleFormChange("regionalApprovers", updated);
                      }}
                      size="small"
                      sx={{ flex: 1 }}
                      renderInput={(params) => (
                        <TextField {...params} label="User" size="small" />
                      )}
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={() => {
                        const updated = formData.regionalApprovers.filter((_, i) => i !== idx);
                        handleFormChange("regionalApprovers", updated);
                      }}
                    >
                      Remove
                    </Button>
                  </Stack>
                ))}
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    handleFormChange("regionalApprovers", [
                      ...formData.regionalApprovers,
                      { region: "", approverUserId: null },
                    ])
                  }
                  sx={{ mt: 0.5 }}
                >
                  + Add Region
                </Button>
              </Box>
            )}

            {formData.approverType === "ROLE" && (
              <FormControl fullWidth size="small">
                <FormLabel>Approver Role</FormLabel>
                <Select
                  multiple
                  value={formData.approverRole}
                  onChange={(e) =>
                    handleFormChange("approverRole", e.target.value)
                  }
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((val) => (
                        <Chip key={val} label={val} size="small" />
                      ))}
                    </Box>
                  )}
                >
                  {APPROVER_ROLE_OPTIONS.map((role) => (
                    <MenuItem key={role} value={role}>
                      {role}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              label="Can Send Back To (comma-separated)"
              value={formData.canSendBackTo.join(",")}
              onChange={(e) =>
                handleFormChange("canSendBackTo", e.target.value.split(","))
              }
              fullWidth
              size="small"
              placeholder="e.g., 1, 2, 3"
              helperText="Steps approver can send drop back to"
            />

            <TextField
              label="Send Email To (comma-separated)"
              value={formData.sendEmailTo}
              onChange={(e) => handleFormChange("sendEmailTo", e.target.value)}
              fullWidth
              size="small"
              placeholder="e.g., user1@subway.com, user2@subway.com"
              helperText="Extra emails CC'd when this step is approved, in addition to the next step's approver(s)"
            />

            {/* ─── Reminder cron overrides ─────────────────────────────────── */}
            <Box>
              <FormLabel sx={{ mb: 1, display: "block" }}>
                Step-inactivity Reminders
              </FormLabel>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Reminder Hours"
                  type="number"
                  value={formData.reminderHours}
                  onChange={(e) => handleFormChange("reminderHours", e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder="Global default"
                  inputProps={{ min: 1 }}
                />
                <TextField
                  label="Max Reminders"
                  type="number"
                  value={formData.maxReminders}
                  onChange={(e) => handleFormChange("maxReminders", e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder="Global default"
                  inputProps={{ min: 1 }}
                />
                <FormControl size="small" sx={{ flex: 1, minWidth: 160 }}>
                  <InputLabel id="auto-approve-after-stale-label">Auto-approve when stale</InputLabel>
                  <Select
                    labelId="auto-approve-after-stale-label"
                    label="Auto-approve when stale"
                    value={!!formData.autoApproveAfterStale}
                    onChange={(e) =>
                      handleFormChange("autoApproveAfterStale", e.target.value)
                    }
                  >
                    <MenuItem value={true}>Enabled</MenuItem>
                    <MenuItem value={false}>Disabled</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  label="Auto-approve Delay (hours)"
                  type="number"
                  value={formData.autoApproveHours}
                  onChange={(e) => handleFormChange("autoApproveHours", e.target.value)}
                  size="small"
                  sx={{ flex: 1 }}
                  placeholder="Global default"
                  inputProps={{ min: 0 }}
                  disabled={!formData.autoApproveAfterStale}
                />
              </Stack>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                Leave hours/max blank to use the global reminder-cron default. When "Auto-approve
                when stale" is enabled, this step is approved automatically (as System) once its
                reminders run out and it stays untouched for the delay above — a separate,
                usually much shorter window than the reminder spacing itself (e.g. 2 reminders a
                day apart, then auto-approve just 2–3h after the last one). Only enable this for
                steps with no required uploads/inputs.
              </Typography>
            </Box>

            {/* ─── Editable files (per-key edit-lock) ──────────────────────── */}
            <FormControl fullWidth size="small">
              <FormLabel>Editable Files</FormLabel>
              <TextField
                multiline
                minRows={3}
                size="small"
                value={editableFilesText}
                onChange={(e) => setEditableFilesText(e.target.value)}
                placeholder={'{\n  "leaseAgreement": false\n}'}
                sx={{ mt: 1, "& textarea": { fontFamily: "monospace", fontSize: 13 } }}
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                Per-input lock map as JSON — set a key to <strong>false</strong> to lock it from
                edits after submission (omitted keys stay editable).
                {editableKeys.length ? ` Keys on this step: ${editableKeys.join(", ")}.` : ""}
              </Typography>
            </FormControl>

            <FormControl fullWidth size="small">
              <FormLabel>CF Status</FormLabel>
              <Select
                value={formData.cfStatus}
                onChange={(e) => handleFormChange("cfStatus", e.target.value)}
              >
                {CF_STATUS_OPTIONS.map((status) => (
                  <MenuItem key={status.value} value={status.value}>
                    {status.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* ─── Input Fields editor ─────────────────────────────────── */}
            <Box>
              <FormLabel sx={{ mb: 1, display: "block" }}>Input Fields</FormLabel>
              {(formData.inputFields || []).map((field, idx) => (
                <Box
                  key={idx}
                  sx={{ border: "1px solid #e0e0e0", borderRadius: 1, p: 1.5, mb: 1.5 }}
                >
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      label="Key"
                      value={field.key}
                      onChange={(e) => updateInputField(idx, { key: e.target.value })}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 130 }}>
                      <Select
                        value={field.type}
                        onChange={(e) => {
                          const type = e.target.value;
                          updateInputField(idx, {
                            type,
                            ...(type === "REPEATABLE" && !field.itemFields ? { itemFields: [] } : {}),
                          });
                        }}
                      >
                        {INPUT_FIELD_TYPES.map((t) => (
                          <MenuItem key={t} value={t}>
                            {t}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button size="small" color="error" onClick={() => removeInputField(idx)}>
                      Remove
                    </Button>
                  </Stack>
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <TextField
                      label="Label"
                      value={field.label}
                      onChange={(e) => updateInputField(idx, { label: e.target.value })}
                      size="small"
                      sx={{ flex: 1 }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={!!field.required}
                        onChange={(e) => updateInputField(idx, { required: e.target.value })}
                      >
                        <MenuItem value={true}>Required</MenuItem>
                        <MenuItem value={false}>Optional</MenuItem>
                      </Select>
                    </FormControl>
                  </Stack>

                  <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                    <FormLabel>Show/Require Only When Another Field Is...</FormLabel>
                    <Select
                      value={!!field.showWhen}
                      onChange={(e) => toggleShowWhen(idx, e.target.value)}
                    >
                      <MenuItem value={false}>Always</MenuItem>
                      <MenuItem value={true}>Conditionally</MenuItem>
                    </Select>
                  </FormControl>
                  {field.showWhen && (
                    <Stack direction="row" spacing={1} sx={{ mb: 1, pl: 1, borderLeft: "2px solid #eee" }}>
                      <TextField
                        label="Depends On Field Key"
                        value={field.showWhen.key || ""}
                        onChange={(e) => updateShowWhen(idx, { key: e.target.value })}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                      <TextField
                        label="Shown When Value In (comma-separated)"
                        value={toCsv(field.showWhen.valueIn)}
                        onChange={(e) => updateShowWhen(idx, { valueIn: fromCsv(e.target.value) })}
                        size="small"
                        sx={{ flex: 1 }}
                      />
                    </Stack>
                  )}

                  {field.type === "DROPDOWN" ? (
                    <>
                      <TextField
                        label="Options (comma-separated)"
                        value={toCsv(field.options)}
                        onChange={(e) =>
                          updateInputField(idx, { options: fromCsv(e.target.value) })
                        }
                        size="small"
                        fullWidth
                        sx={{ mb: 1 }}
                      />
                      <FormControl fullWidth size="small" sx={{ mb: 1 }}>
                        <FormLabel>Conditional Upload</FormLabel>
                        <Select
                          value={!!field.fileUpload}
                          onChange={(e) => toggleConditionalUpload(idx, e.target.value)}
                        >
                          <MenuItem value={true}>Enabled</MenuItem>
                          <MenuItem value={false}>Disabled</MenuItem>
                        </Select>
                      </FormControl>
                      {field.fileUpload && (
                        <Stack spacing={1} sx={{ pl: 1, borderLeft: "2px solid #eee" }}>
                          <TextField
                            label="Upload Key"
                            value={field.fileUpload.key || ""}
                            onChange={(e) => updateFileUpload(idx, { key: e.target.value })}
                            size="small"
                          />
                          <TextField
                            label="Upload Label"
                            value={field.fileUpload.label || ""}
                            onChange={(e) => updateFileUpload(idx, { label: e.target.value })}
                            size="small"
                          />
                          <TextField
                            label="Required when value in (comma-separated)"
                            value={toCsv(field.fileUpload.whenValueIn)}
                            onChange={(e) =>
                              updateFileUpload(idx, { whenValueIn: fromCsv(e.target.value) })
                            }
                            size="small"
                          />
                          <TextField
                            label="Accept"
                            value={field.fileUpload.accept || ""}
                            onChange={(e) => updateFileUpload(idx, { accept: e.target.value })}
                            size="small"
                          />
                        </Stack>
                      )}
                    </>
                  ) : field.type === "REPEATABLE" ? (
                    <Box sx={{ pl: 1, borderLeft: "2px solid #eee" }}>
                      <FormLabel sx={{ mb: 1, display: "block" }}>Repeated Entry Fields</FormLabel>
                      {(field.itemFields || []).map((itemField, itemIdx) => (
                        <Stack
                          key={itemIdx}
                          direction="row"
                          spacing={1}
                          alignItems="center"
                          sx={{ mb: 1 }}
                        >
                          <TextField
                            label="Key"
                            value={itemField.key}
                            onChange={(e) => updateItemField(idx, itemIdx, { key: e.target.value })}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <Select
                              value={itemField.type}
                              onChange={(e) => updateItemField(idx, itemIdx, { type: e.target.value })}
                            >
                              {ITEM_FIELD_TYPES.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                          <TextField
                            label="Label"
                            value={itemField.label}
                            onChange={(e) => updateItemField(idx, itemIdx, { label: e.target.value })}
                            size="small"
                            sx={{ flex: 1 }}
                          />
                          <FormControl size="small" sx={{ minWidth: 110 }}>
                            <Select
                              value={!!itemField.required}
                              onChange={(e) => updateItemField(idx, itemIdx, { required: e.target.value })}
                            >
                              <MenuItem value={true}>Required</MenuItem>
                              <MenuItem value={false}>Optional</MenuItem>
                            </Select>
                          </FormControl>
                          {itemField.type === "UPLOAD" && (
                            <TextField
                              label="Accept"
                              value={itemField.accept || ""}
                              onChange={(e) => updateItemField(idx, itemIdx, { accept: e.target.value })}
                              size="small"
                              sx={{ flex: 1 }}
                            />
                          )}
                          <Button size="small" color="error" onClick={() => removeItemField(idx, itemIdx)}>
                            Remove
                          </Button>
                        </Stack>
                      ))}
                      <Button size="small" variant="outlined" onClick={() => addItemField(idx)}>
                        + Add Item Field
                      </Button>
                    </Box>
                  ) : (
                    <TextField
                      label="Accept"
                      value={field.accept || ""}
                      onChange={(e) => updateInputField(idx, { accept: e.target.value })}
                      size="small"
                      fullWidth
                    />
                  )}
                </Box>
              ))}
              <Button size="small" variant="outlined" onClick={addInputField}>
                + Add Input Field
              </Button>
            </Box>

            <Stack
              direction="row"
              spacing={2}
              sx={{ mt: 2, justifyContent: "flex-end" }}
            >
              <Button variant="outlined" onClick={() => setEditModal(false)}>
                Cancel
              </Button>
              <Button variant="contained" onClick={formSubmitHandler}>
                Update
              </Button>
            </Stack>
          </Box>
        </CustomModal>
      )}

      {/* View Modal */}
      {viewModal && (
        <CustomModal
          open={viewModal}
          onClose={() => setViewModal(false)}
          title={`Step ${editData.stepNumber} Details`}
          size="md"
        >
          <Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <strong>Name:</strong> {editData.name}
            </Box>
            <Box>
              <strong>Approver Type:</strong> {editData.approverType}
            </Box>
            {editData.approverType === "USER" && (
              <Box>
                <strong>Approver User IDs:</strong> {getStepApproverUserIds(editData).join(", ")}
              </Box>
            )}
            {editData.approverType === "ROLE" && (
              <Box>
                <strong>Approver Roles:</strong>
                <Box sx={{ display: "flex", gap: 1, mt: 1, flexWrap: "wrap" }}>
                  {editData.approverRole?.map((role) => (
                    <Chip key={role} label={role} size="small" />
                  ))}
                </Box>
              </Box>
            )}
            <Box>
              <strong>Editable Files:</strong>{" "}
              {editData.editableFilesObject &&
              Object.keys(editData.editableFilesObject).length > 0 ? (
                Object.entries(editData.editableFilesObject).map(([k, v]) => (
                  <Chip
                    key={k}
                    label={`${k}: ${v === false ? "Locked" : "Editable"}`}
                    color={v === false ? "warning" : "success"}
                    size="small"
                    sx={{ mr: 0.5, mb: 0.5 }}
                  />
                ))
              ) : (
                <span>—</span>
              )}
            </Box>
            <Box>
              <strong>Status:</strong>{" "}
              <Chip
                label={editData.isActive ? "Active" : "Inactive"}
                color={editData.isActive ? "success" : "error"}
                size="small"
              />
            </Box>
            <Box>
              <strong>Routing Rules:</strong>
              <Box sx={{ mt: 1, pl: 2 }}>
                {editData.canSendBackTo &&
                  editData.canSendBackTo.length > 0 && (
                    <div>
                      ✓ Approver can choose to send back to:{" "}
                      {editData.canSendBackTo.join(", ")}
                    </div>
                  )}
                {(!editData.canSendBackTo ||
                  editData.canSendBackTo.length === 0) && (
                  <div>No special routing configured</div>
                )}
              </Box>
            </Box>
            <Box>
              <strong>Send Email To:</strong>{" "}
              {editData.sendEmailTo ? editData.sendEmailTo : "—"}
            </Box>
            <Box>
              <strong>Step-inactivity Reminders:</strong>{" "}
              {editData.reminderHours ?? "Global default"}h, max{" "}
              {editData.maxReminders ?? "global default"}
              {editData.autoApproveAfterStale && (
                <>
                  <Chip
                    label={`Auto-approves ${editData.autoApproveHours ?? "global default"}h after last reminder`}
                    color="warning"
                    size="small"
                    sx={{ ml: 1 }}
                  />
                </>
              )}
            </Box>
          </Box>
        </CustomModal>
      )}
    </Box>
  );
};

export default StepMaster;
